import React, { useState, memo, useCallback, useMemo } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Form, 
  Alert, 
  Badge,
  Nav,
  Tab,
  Tabs
} from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaCube, 
  FaEthereum, 
  FaShieldAlt, 
  FaCode, 
  FaRocket,
  FaUsers,
  FaCertificate,
  FaGithub,
  FaLinkedin,
  FaTwitter,
  FaDiscord,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaGraduationCap,
  FaBitcoin,
  FaChartLine,
  FaCogs,
  FaLock,
  FaGlobe,
  FaExchangeAlt,
  FaLaptopCode 
} from 'react-icons/fa';
import {  backend} from '../config/api';
import logo from "../DarkLearn.jpg"
import axios from 'axios';

const Dashboard = memo(() => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    age: '',
    address: '',
    college: '',
    educationDetails: '',
    linkedinProfile: '',
    githubProfile: '',
    interests: '',
    walletAddress: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('home');

  const features = useMemo(() => [
  {
    icon: <FaChartLine />,
    title: "Market Analysis",
    description: "Advanced cryptocurrency market analysis and trading strategies"
  },
  {
    icon: <FaLock />,
    title: "Security First",
    description: "Comprehensive security protocols and best practices"
  },
  {
    icon: <FaGlobe />,
    title: "Global Community",
    description: "Connect with crypto enthusiasts and developers worldwide"
  },
  {
    icon: <FaExchangeAlt />,
    title: "DeFi Innovation",
    description: "Stay ahead with the latest DeFi protocols and innovations"
  },
  {
    icon: <FaCode />,  // Make sure to import FaCode
    title: "Full Stack Development",
    description: "Master modern front-end and back-end technologies for production-ready apps"
  },
  {
    icon: <FaLaptopCode />,  // Make sure to import FaLaptopCode
    title: "Web3 & Coding",
    description: "Build decentralized apps with Solidity, Web3.js, and smart contract integration"
  }
], []);

  const stats = useMemo(() => [
    { number: "5000+", label: "Crypto Traders", icon: <FaBitcoin /> },
    { number: "100+", label: "DeFi Projects", icon: <FaEthereum /> },
    { number: "98%", label: "Security Rate", icon: <FaShieldAlt /> },
    { number: "$50M+", label: "Assets Secured", icon: <FaLock /> }
  ], []);

  const validateForm = useCallback(() => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
    if (!formData.age.trim()) {
      newErrors.age = 'Age is required';
    } else if (isNaN(formData.age) || formData.age < 16 || formData.age > 100) {
      newErrors.age = 'Please enter a valid age (16-100)';
    }
    if (!formData.address.trim()) newErrors.address = 'Address is required';

    return newErrors;
  }, [formData]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  }, [errors]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      console.log("backend.register",`${backend}/api/users/register`)
      const response = await axios.post(`${backend}/api/users/register`, formData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      setMessage({ 
        type: 'success', 
        text: 'Registration successful! Welcome to DarkLearn Academy.' 
      });
      
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        age: '',
        address: '',
        college: '',
        educationDetails: '',
        linkedinProfile: '',
        githubProfile: '',
        interests: '',
        walletAddress: ''
      });
      
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const floatingVariants = {
    animate: {
      y: [-20, 20, -20],
      rotate: [0, 5, 0],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const renderHome = () => (
    <>
      {/* Hero Section */}
      <motion.section 
        className="py-5"
        style={{ paddingTop: '60px' }}
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <Container>
          <Row className="align-items-center" style={{ minHeight: '70vh' }}>
            <Col lg={6}>
              <motion.div variants={itemVariants}>
                <Badge bg="warning" className="mb-3 p-2">
                  🔥 New: Advanced DeFi Yield Strategies
                </Badge>
                <h1 className="display-4 fw-bold text-white mb-4">
                  Master 
                  <span className="text-warning d-block">Cryptocurrency & DeFi</span>
                </h1>
                <p className="lead text-white-50 mb-4">
                  Join Dark Learn Academy for comprehensive training in cryptocurrency trading, 
                  DeFi protocols, DApp development, and blockchain security. Build the future of finance.
                </p>
                <div className="d-flex gap-3 flex-wrap">
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button 
                      size="lg" 
                      className="px-4 py-3"
                      style={{
                        background: 'linear-gradient(45deg, #FF6B35 30%, #F7931E 90%)',
                        border: 'none'
                      }}
                      onClick={() => setActiveTab('register')}
                    >
                      Start Trading Today
                    </Button>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button 
                      variant="outline-light" 
                      size="lg" 
                      className="px-4 py-3"
                      onClick={() => setActiveTab('about')}
                    >
                      Learn More
                    </Button>
                  </motion.div>
                </div>
              </motion.div>
            </Col>
            <Col lg={6}>
              <motion.div 
                className="position-relative"
                variants={itemVariants}
              >
                <motion.div
                  variants={floatingVariants}
                  animate="animate"
                  className="position-absolute top-0 start-0"
                  style={{ zIndex: 1 }}
                >
                  <Card className="border-0 shadow-lg bg-warning text-dark p-3">
                    <FaBitcoin size={40} />
                  </Card>
                </motion.div>
                <motion.div
                  variants={floatingVariants}
                  animate="animate"
                  className="position-absolute top-50 end-0"
                  style={{ zIndex: 1, animationDelay: '2s' }}
                >
                  <Card className="border-0 shadow-lg bg-info text-white p-3">
                    <FaEthereum size={40} />
                  </Card>
                </motion.div>
                <motion.div
                  variants={floatingVariants}
                  animate="animate"
                  className="position-absolute bottom-0 start-50"
                  style={{ zIndex: 1, animationDelay: '4s' }}
                >
                  <Card className="border-0 shadow-lg bg-success text-white p-3">
                    <FaShieldAlt size={40} />
                  </Card>
                </motion.div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </motion.section>

      {/* Stats Section */}
      <motion.section 
        className="py-5 bg-dark bg-opacity-50"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={containerVariants}
      >
        <Container>
          <Row>
            {stats.map((stat, index) => (
              <Col md={3} key={index} className="mb-4">
                <motion.div variants={itemVariants}>
                  <Card className="border-0 bg-transparent text-center">
                    <Card.Body>
                      <div className="text-warning mb-2" style={{ fontSize: '2rem' }}>
                        {stat.icon}
                      </div>
                      <h3 className="text-white fw-bold">{stat.number}</h3>
                      <p className="text-white-50 mb-0">{stat.label}</p>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </motion.section>

      {/* Features Section */}
      <motion.section 
        className="py-5"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={containerVariants}
      >
        <Container>
          <Row>
            <Col lg={8} className="mx-auto text-center mb-5">
              <motion.div variants={itemVariants}>
                <h2 className="display-5 fw-bold text-white mb-4">
                  Comprehensive Crypto Education
                </h2>
                <p className="lead text-white-50">
                  From cryptocurrency basics to advanced DeFi protocols and DApp development
                </p>
              </motion.div>
            </Col>
          </Row>
          <Row>
            {features.map((feature, index) => (
              <Col lg={6} key={index} className="mb-4">
                <motion.div 
                  variants={itemVariants}
                  whileHover={{ 
                    y: -10,
                    transition: { duration: 0.3 }
                  }}
                >
                  <Card className="h-100 border-0 shadow-lg bg-white bg-opacity-10 text-white" style={{ backdropFilter: 'blur(10px)' }}>
                    <Card.Body className="p-4">
                      <div className={`text-${feature.color} mb-3`} style={{ fontSize: '3rem' }}>
                        {feature.icon}
                      </div>
                      <h4 className="fw-bold mb-3">{feature.title}</h4>
                      <p className="text-white-50 mb-0">{feature.description}</p>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </motion.section>
    </>
  );

  const renderAbout = () => (
    <motion.section 
      className="py-5"
      style={{ paddingTop: '60px', minHeight: '100vh' }}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <Container>
        <Row className="mb-5">
          <Col lg={8} className="mx-auto text-center">
            <motion.div variants={itemVariants}>
              <h1 className="display-4 fw-bold text-white mb-4">
                About <span className="text-warning">DarkLearn</span>
              </h1>
              <p className="lead text-white-50 mb-4">
                Academy is the premier destination for cryptocurrency education, 
                DeFi protocol development, and blockchain security training. We empower 
                the next generation of crypto innovators.
              </p>
            </motion.div>
          </Col>
        </Row>

        <Row className="mb-5">
          <Col lg={6} className="mb-4">
            <motion.div variants={itemVariants}>
              <Card className="border-0 shadow-lg bg-white bg-opacity-10 text-white h-100" style={{ backdropFilter: 'blur(10px)' }}>
                <Card.Body className="p-4">
                  <h3 className="text-warning mb-3">Our Mission</h3>
                  <p className="text-white-50">
                    To democratize access to advanced cryptocurrency knowledge and DeFi protocols, 
                    making blockchain technology accessible to everyone while maintaining the highest 
                    security standards.
                  </p>
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
          <Col lg={6} className="mb-4">
            <motion.div variants={itemVariants}>
              <Card className="border-0 shadow-lg bg-white bg-opacity-10 text-white h-100" style={{ backdropFilter: 'blur(10px)' }}>
                <Card.Body className="p-4">
                  <h3 className="text-warning mb-3">Our Vision</h3>
                  <p className="text-white-50">
                    To become the leading platform for cryptocurrency education and DeFi innovation, 
                    fostering a global community of skilled blockchain developers and traders.
                  </p>
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
        </Row>



        

        <Row className="mb-5">
          <Col lg={12}>
            <motion.div variants={itemVariants} className="text-center mb-4">
              <h2 className="display-6 fw-bold text-white mb-4">Why Choose DarkLearn?</h2>
            </motion.div>
          </Col>
          {features.map((feature, index) => (
            <Col lg={3} md={6} key={index} className="mb-4">
              <motion.div 
                variants={itemVariants}
                whileHover={{ 
                  y: -10,
                  transition: { duration: 0.3 }
                }}
              >
                <Card className="border-0 shadow-lg bg-white bg-opacity-10 text-white text-center h-100" style={{ backdropFilter: 'blur(10px)' }}>
                  <Card.Body className="p-4">
                    <div className="text-warning mb-3" style={{ fontSize: '2.5rem' }}>
                      {feature.icon}
                    </div>
                    <h5 className="fw-bold mb-3">{feature.title}</h5>
                    <p className="text-white-50 mb-0">{feature.description}</p>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          ))}
        </Row>



        <Row>
          <Col lg={8} className="mx-auto text-center">
            <motion.div variants={itemVariants}>
              <Card className="border-0 shadow-lg bg-warning text-dark">
                <Card.Body className="p-5">
                  <h3 className="fw-bold mb-3">Ready to Start Your Crypto Journey?</h3>
                  <p className="mb-4">
                    Join thousands of successful traders and developers who have transformed 
                    their careers with DarkLearn Academy.
                  </p>
                  <Button 
                    size="lg" 
                    variant="dark"
                    className="px-4 py-3" 
                    onClick={() => setActiveTab('register')}
                  >
                    Join  DarkLearn Today
                  </Button>
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </motion.section>
  );

  const renderRegister = () => (
    <motion.section 
   className="py-5 mt-5 mt-md-0" 
      style={{ paddingTop: '60px', minHeight: '100vh' }}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <Container >
        <Row className="justify-content-center">
          <Col xs={12} sm={10} md={8} lg={6}> 
            <motion.div variants={itemVariants}>
              <Card className="border-0 shadow-lg">
                <Card.Header className="bg-warning text-dark text-center py-4">
                  <h3 className="mb-0 fw-bold"> DarkLearn Academy</h3>
                  <p className="mb-0">Join With Us</p>
                </Card.Header>
                <Card.Body className="p-4">
                  {message.text && (
                    <Alert 
                      variant={message.type === 'success' ? 'success' : 'danger'}
                      className="mb-4"
                    >
                      {message.text}
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit} >
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">
                            <FaUser className="me-2 text-warning" />
                            First Name *
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            isInvalid={!!errors.firstName}
                            placeholder="Enter your first name"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.firstName}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">
                            <FaUser className="me-2 text-warning" />
                            Last Name *
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            isInvalid={!!errors.lastName}
                            placeholder="Enter your last name"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.lastName}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">
                            <FaEnvelope className="me-2 text-warning" />
                            Email *
                          </Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            isInvalid={!!errors.email}
                            placeholder="Enter your email"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.email}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">
                            <FaPhone className="me-2 text-warning" />
                            Phone Number *
                          </Form.Label>
                          <Form.Control
                            type="tel"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleChange}
                            isInvalid={!!errors.phoneNumber}
                            placeholder="Enter your phone number"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.phoneNumber}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Age *</Form.Label>
                          <Form.Control
                            type="number"
                            name="age"
                            value={formData.age}
                            onChange={handleChange}
                            isInvalid={!!errors.age}
                            placeholder="Enter your age"
                            min="16"
                            max="100"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.age}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">
                            <FaMapMarkerAlt className="me-2 text-warning" />
                            Address *
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            isInvalid={!!errors.address}
                            placeholder="Enter your address"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.address}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        <FaGraduationCap className="me-2 text-warning" />
                        College/University
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="college"
                        value={formData.college}
                        onChange={handleChange}
                        placeholder="Enter your college or university"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Education Details</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="educationDetails"
                        value={formData.educationDetails}
                        onChange={handleChange}
                        placeholder="Share your educational background"
                      />
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">
                            <FaLinkedin className="me-2 text-warning" />
                            LinkedIn Profile
                          </Form.Label>
                          <Form.Control
                            type="url"
                            name="linkedinProfile"
                            value={formData.linkedinProfile}
                            onChange={handleChange}
                            placeholder="https://linkedin.com/in/yourprofile"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">
                            <FaGithub className="me-2 text-warning" />
                            GitHub Profile
                          </Form.Label>
                          <Form.Control
                            type="url"
                            name="githubProfile"
                            value={formData.githubProfile}
                            onChange={handleChange}
                            placeholder="https://github.com/yourusername"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Crypto Interests</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="interests"
                        value={formData.interests}
                        onChange={handleChange}
                        placeholder="What aspects of cryptocurrency and DeFi interest you?"
                      />
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold">
                        <FaEthereum className="me-2 text-warning" />
                        Crypto Wallet Address
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="walletAddress"
                        value={formData.walletAddress}
                        onChange={handleChange}
                        placeholder="0x... (optional)"
                      />
                    </Form.Group>




                    

                    <div className="text-center">
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Button
                          type="submit"
                          size="lg"
                          className="px-5 py-3"
                          style={{
                            background: 'linear-gradient(45deg, #FF6B35 30%, #F7931E 90%)',
                            border: 'none'
                          }}
                          disabled={loading}
                        >
                          {loading ? 'Creating Account...' : ' DarkLearn Academy'}
                        </Button>
                      </motion.div>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </motion.section>
  );

  return (
    <div style={{ 
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      minHeight: '100vh'
    }}>
      {/* Navigation Tabs */}
     {/* Navigation Tabs */}
<div className="bg-dark bg-opacity-75 fixed-top" style={{ backdropFilter: 'blur(10px)' }}>
  <Container>
    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center py-2 py-md-3">
      <div className="d-flex align-items-center mb-2 mb-md-0">
        <img src={logo} alt="Logo" className="me-2" style={{ width: 30, height: 30 }} />
        <h4 className="mb-0 fw-bold text-white">DarkLearn Academy</h4>
      </div>

      <Nav
        variant="pills"
        activeKey={activeTab}
        onSelect={setActiveTab}
        className="flex-nowrap"
      >
        <Nav.Item>
          <Nav.Link eventKey="home" className="text-white px-2 px-md-3">Home</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="about" className="text-white px-2 px-md-3">About</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="register" className="text-white px-2 px-md-3">Register</Nav.Link>
        </Nav.Item>
      </Nav>
    </div>
  </Container>
</div>

      {/* Content based on active tab */}
      {activeTab === 'home' && renderHome()}
      {activeTab === 'about' && renderAbout()}
      {activeTab === 'register' && renderRegister()}

      {/* Footer */}
      <footer className="bg-dark text-white py-5">
        <Container>
          <Row>
            <Col lg={4} className="mb-4">
              <div className="d-flex align-items-center mb-3">
                <FaCube className="me-2 text-warning" size={30} />
 

                <h4 className="mb-0 fw-bold">DarkLearn Academy</h4>
              </div>
              <p className="text-white-50">
                Empowering the next generation of crypto traders and DeFi developers with 
                comprehensive education in cryptocurrency, DeFi protocols, and blockchain security.
              </p>
              <div className="d-flex gap-3">
                <motion.a 
                  href="#" 
                  className="text-warning"
                  whileHover={{ scale: 1.2 }}
                >
                  <FaGithub size={24} />
                </motion.a>
                <motion.a 
                  href="#" 
                  className="text-warning"
                  whileHover={{ scale: 1.2 }}
                >
                  <FaLinkedin size={24} />
                </motion.a>
                <motion.a 
                  href="#" 
                  className="text-warning"
                  whileHover={{ scale: 1.2 }}
                >
                  <FaTwitter size={24} />
                </motion.a>
                <motion.a 
                  href="#" 
                  className="text-warning"
                  whileHover={{ scale: 1.2 }}
                >
                  <FaDiscord size={24} />
                </motion.a>
              </div>
            </Col>
            <Col lg={2} className="mb-4">
              <h5 className="fw-bold mb-3">Programs</h5>
              <ul className="list-unstyled">
                <li><a href="#" className="text-white-50 text-decoration-none">Crypto Trading</a></li>
                <li><a href="#" className="text-white-50 text-decoration-none">DeFi Protocols</a></li>
                <li><a href="#" className="text-white-50 text-decoration-none">DApp Development</a></li>
                <li><a href="#" className="text-white-50 text-decoration-none">Security Auditing</a></li>
              </ul>
            </Col>
            <Col lg={2} className="mb-4">
              <h5 className="fw-bold mb-3">Resources</h5>
              <ul className="list-unstyled">
                <li><a href="#" className="text-white-50 text-decoration-none">Documentation</a></li>
                <li><a href="#" className="text-white-50 text-decoration-none">Community</a></li>
                <li><a href="#" className="text-white-50 text-decoration-none">Blog</a></li>
                <li><a href="#" className="text-white-50 text-decoration-none">Support</a></li>
              </ul>
            </Col>
            <Col lg={2} className="mb-4">
              <h5 className="fw-bold mb-3">Company</h5>
              <ul className="list-unstyled">
                <li><a href="#" className="text-white-50 text-decoration-none">About Us</a></li>
                <li><a href="#" className="text-white-50 text-decoration-none">Careers</a></li>
                <li><a href="#" className="text-white-50 text-decoration-none">Contact</a></li>
                <li><a href="#" className="text-white-50 text-decoration-none">Privacy</a></li>
              </ul>
            </Col>
            <Col lg={2} className="mb-4">
              <h5 className="fw-bold mb-3">Contact</h5>
              <p className="text-white-50 small">
                <FaEnvelope className="me-2" />
                info@DarkLearn.academy
              </p>
              <p className="text-white-50 small">
                <FaPhone className="me-2" />
                +1 (555) 123-4567
              </p>
              <p className="text-white-50 small">
                <FaMapMarkerAlt className="me-2" />
                San Francisco, CA
              </p>
            </Col>
          </Row>
          <hr className="my-4 border-secondary" />
          <Row>
            <Col className="text-center">
              <p className="text-white-50 mb-0">
                © 2024 DarkLearn Academy. All rights reserved. Built with ❤️ for the crypto community.
              </p>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
});

Dashboard.displayName = 'Dashboard';

export default Dashboard;